import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
} from "@tanstack/react-query";
import { AnimatedImagePresetApi } from "../api/animated-image-preset-api";
import {
  UpdateAnimatedImagePresetDto,
  PagedAnimatedImagePresetsResponseDto,
} from "@repo/api/animated-image-preset";
import { queryClient } from "./query-client";
import Toast from "../components/Toast";

export type AnimatedImagePresetsFilterParams = {
  tags?: string[];
  isFavourite?: boolean;
};

type AnimatedImagePresetCursor =
  | { createdAt: string; externalId: string }
  | undefined;

export function usePagedAnimatedImagePresetsQuery(
  teamExternalId: string,
  filterParams: AnimatedImagePresetsFilterParams,
  pageSize: number,
) {
  return useInfiniteQuery({
    queryKey: animatedImagePresetQueryKeys.paged(
      teamExternalId,
      filterParams,
      pageSize,
    ),
    queryFn: ({ pageParam }) =>
      AnimatedImagePresetApi.getPagedAnimatedImagePresets(teamExternalId, {
        ...filterParams,
        pageSize,
        cursorCreatedAt: pageParam?.createdAt,
        cursorExternalId: pageParam?.externalId,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as AnimatedImagePresetCursor,
    staleTime: 0,
    meta: {
      errorMessage: "Failed to fetch animation presets",
    },
  });
}

export function useUpdateAnimatedImagePresetMutation({
  teamExternalId,
  onSuccess,
}: {
  teamExternalId: string;
  onSuccess?: () => void;
}) {
  return useMutation({
    mutationKey:
      animatedImagePresetQueryKeys.updateAnimatedImagePreset(teamExternalId),
    mutationFn: ({
      presetExternalId,
      data,
    }: {
      presetExternalId: string;
      data: UpdateAnimatedImagePresetDto;
    }) =>
      AnimatedImagePresetApi.updateAnimatedImagePreset(
        teamExternalId,
        presetExternalId,
        data,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: animatedImagePresetQueryKeys.all(),
      });
      onSuccess?.();
    },
    onError: () => {
      Toast.error("Failed to update prompt");
    },
  });
}

export function useDeleteAnimatedImagePresetMutation({
  teamExternalId,
  onSuccess,
}: {
  teamExternalId: string;
  onSuccess?: () => void;
}) {
  return useMutation({
    mutationKey:
      animatedImagePresetQueryKeys.deleteAnimatedImagePreset(teamExternalId),
    mutationFn: (presetExternalId: string) =>
      AnimatedImagePresetApi.deleteAnimatedImagePreset(
        teamExternalId,
        presetExternalId,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: animatedImagePresetQueryKeys.all(),
      });
      onSuccess?.();
    },
    onError: () => {
      Toast.error("Failed to delete prompt");
    },
  });
}

export function useSetAnimatedImagePresetFavouriteMutation({
  teamExternalId,
}: {
  teamExternalId: string;
}) {
  return useMutation({
    mutationKey:
      animatedImagePresetQueryKeys.setAnimatedImagePresetFavourite(
        teamExternalId,
      ),
    mutationFn: ({
      presetExternalId,
      isFavourite,
    }: {
      presetExternalId: string;
      isFavourite: boolean;
    }) =>
      AnimatedImagePresetApi.setAnimatedImagePresetFavourite(
        teamExternalId,
        presetExternalId,
        { isFavourite },
      ),
    onMutate: async ({ presetExternalId, isFavourite }) => {
      const queryKey = animatedImagePresetQueryKeys.all();
      await queryClient.cancelQueries({ queryKey });
      const snapshots = queryClient.getQueriesData<
        InfiniteData<PagedAnimatedImagePresetsResponseDto>
      >({ queryKey });
      queryClient.setQueriesData<
        InfiniteData<PagedAnimatedImagePresetsResponseDto>
      >({ queryKey }, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            presets: page.presets.map((preset) =>
              preset.externalId === presetExternalId
                ? { ...preset, isFavourite }
                : preset,
            ),
          })),
        };
      });
      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      if (context?.snapshots) {
        for (const [key, data] of context.snapshots) {
          queryClient.setQueryData(key, data);
        }
      }
      Toast.error("Failed to update favourite");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: animatedImagePresetQueryKeys.all(),
      });
    },
  });
}

export const animatedImagePresetQueryKeys = {
  all: () => ["animatedImagePreset"],
  paged: (
    teamExternalId: string,
    filterParams: AnimatedImagePresetsFilterParams,
    pageSize: number,
  ) => [
    "animatedImagePreset",
    "paged",
    { teamExternalId, ...filterParams, pageSize },
  ],
  updateAnimatedImagePreset: (teamExternalId: string) => [
    "animatedImagePreset",
    "update",
    { teamExternalId },
  ],
  deleteAnimatedImagePreset: (teamExternalId: string) => [
    "animatedImagePreset",
    "delete",
    { teamExternalId },
  ],
  setAnimatedImagePresetFavourite: (teamExternalId: string) => [
    "animatedImagePreset",
    "setFavourite",
    { teamExternalId },
  ],
};
